'use client'
import styles from './MktuButton.module.scss'
import { MktuClass } from '@/services/Api/generated'
import Link from 'next/link'
import cn from 'classnames'
import React, {
  FunctionComponent,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  autoUpdate,
  ExtendedRefs,
  flip,
  FloatingPortal,
  offset,
  ReferenceType,
  shift,
  useFloating,
} from '@floating-ui/react'
import { useDocumentClick } from '@/hooks/DOM'

enum MktuButtonTypes {
  simple = 'simple',
  withPopup = 'withPopup',
}

type MktuButtonProps = {
  children?: (props: {
    refs: ExtendedRefs<ReferenceType>
    floatingStyles: React.CSSProperties
    isPopupVisible: boolean
  }) => ReactNode
  mktuClass: MktuClass
  // Для необходимости отслеживания ховер-эффекта и активации
  isActive?: boolean
  onHoverClass?: (selectedClass: MktuClass) => void
  onSelectClass?: (selectedClass: MktuClass) => void
  onLeaveHoverMktuClass?: () => void
  disableLink?: boolean
}
export const MktuButton: FunctionComponent<MktuButtonProps> = ({
  children,
  mktuClass,
  isActive,
  onHoverClass,
  onSelectClass,
  onLeaveHoverMktuClass,
  disableLink,
}) => {
  const classLink = useMemo(() => {
    return `/mktu/classes/${mktuClass.classNumber}`
  }, [mktuClass.classNumber])

  const buttonType = useMemo(() => {
    if (children != null) {
      return MktuButtonTypes.withPopup
    }

    return MktuButtonTypes.simple
  }, [children])

  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const mouseEnterTimer = useRef<ReturnType<typeof setTimeout>>(
    setTimeout(() => {})
  )
  const mouseLeaveTimer = useRef<ReturnType<typeof setTimeout>>(
    setTimeout(() => {})
  )

  // Floating UI
  const { refs, floatingStyles, update } = useFloating({
    open: isPopupVisible,
    onOpenChange: setIsPopupVisible,
    placement: 'bottom-start',
    whileElementsMounted: isPopupVisible ? autoUpdate : undefined,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  })

  // Закрытие по клику вне
  useDocumentClick((e: any) => {
    if (
      refs.floating.current &&
      !refs.floating.current.contains(e.target) &&
      refs.reference.current &&
      refs.reference.current instanceof Element &&
      !refs.reference.current.contains(e.target)
    ) {
      setIsPopupVisible(false)
    }
  })

  const onMouseLeave = useCallback(() => {
    if (buttonType != MktuButtonTypes.simple) {
      if (window.innerWidth >= 1024) {
        clearTimeout(mouseLeaveTimer.current)
        clearTimeout(mouseEnterTimer.current)
        mouseLeaveTimer.current = setTimeout(() => {
          setIsPopupVisible(false)
        }, 300)
      }
    } else {
      if (typeof onLeaveHoverMktuClass === 'function') {
        onLeaveHoverMktuClass()
      }
    }
  }, [buttonType, onLeaveHoverMktuClass])

  const onMouseEnter = useCallback(() => {
    if (buttonType != MktuButtonTypes.simple) {
      if (window.innerWidth >= 1024) {
        clearTimeout(mouseLeaveTimer.current)
        clearTimeout(mouseEnterTimer.current)
        mouseEnterTimer.current = setTimeout(() => {
          setIsPopupVisible(true)
        }, 300)
      }
    } else {
      if (window.innerWidth >= 1024) {
        if (typeof onHoverClass === 'function' && mktuClass) {
          onHoverClass(mktuClass)
        }
      }
    }
  }, [buttonType, mktuClass, onHoverClass])

  const clickButton: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (event) => {
      event.stopPropagation()
      if (disableLink == true) {
        event.preventDefault()
      }
      if (buttonType != MktuButtonTypes.simple) {
        if (window.innerWidth < 1024) {
          event.preventDefault()
          setIsPopupVisible(true)
        }
      } else {
        event.preventDefault()
        if (typeof onSelectClass === 'function' && mktuClass) {
          onSelectClass(mktuClass)
        }
      }
    },
    [buttonType, disableLink, mktuClass, onSelectClass]
  )

  return (
    <div
      className={styles.container}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      ref={buttonType != MktuButtonTypes.simple ? refs.setReference : null}
    >
      <Link
        href={classLink}
        className={cn(styles.button, {
          [styles.isActive]: isActive,
        })}
        onClick={clickButton}
      >
        {mktuClass.classNumber}
      </Link>
      {buttonType === MktuButtonTypes.withPopup ? (
        <FloatingPortal>
          {children({
            refs,
            floatingStyles,
            isPopupVisible,
          })}
        </FloatingPortal>
      ) : null}
    </div>
  )
}
